import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery as useTanStackQuery } from '@tanstack/react-query';
import { Marker, Polygon } from '@react-google-maps/api';
import { Box, Stack, Typography, DatePicker, FilterSelect, Button, Map } from '../../ui/core';
import { cardHeaderSx, filterPillSx, pillButtonSx } from '../../ui/styles';
import { StationIcon, InfoIcon } from '../../ui/icons';
import { useCities, useQuery } from '../../hooks';
import isochroneApi from '../../lib/api/IsochroneApi';
import { CIRCLE_SYMBOL_PATH } from '../../ui/core/mapSymbolPaths';

const DEFAULT_CENTER = { lat: 30.2672, lng: -97.7431 };
const DEFAULT_ZOOM = 10;

const TYPE_OPTIONS = ['ALL', 'ORIGIN', 'DESTINATION'];

const RIDE_COLORS = [
  '#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D01',
  '#46BDC6', '#7B61FF', '#E91E63', '#00BCD4', '#8BC34A',
  '#FF5722', '#3F51B5', '#009688', '#FFC107', '#795548',
  '#607D8B', '#9C27B0', '#CDDC39', '#F44336', '#2196F3',
];

const getRideColor = (rideId, rideColorMap) => {
  if (!rideColorMap.has(rideId)) {
    rideColorMap.set(rideId, RIDE_COLORS[rideColorMap.size % RIDE_COLORS.length]);
  }
  return rideColorMap.get(rideId);
};

const makePinIcon = (color, isOrigin) => ({
  path: CIRCLE_SYMBOL_PATH,
  fillColor: isOrigin ? color : '#ffffff',
  fillOpacity: 1,
  strokeColor: color,
  strokeWeight: isOrigin ? 2 : 3,
  scale: 8,
});

const updateURLParams = ({ date, type, cityId }, history) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (type && type !== 'ALL') params.append('type', type);
  if (cityId) params.append('cityId', cityId);
  history.push({ search: params.toString() });
};

const Isochrones = () => {
  const history = useHistory();

  let { date, type, cityId } = useQuery();
  date = date || new Date().toLocaleDateString('en-CA');
  type = TYPE_OPTIONS.includes(type) ? type : 'ALL';

  const [map, setMap] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [showPolygons, setShowPolygons] = useState(false);
  const [selectedRideIds, setSelectedRideIds] = useState([]);

  const { cities: citiesOptions } = useCities({ operationalOnly: true, sorted: true });

  const selectedCity = useMemo(() => {
    if (!cityId) return null;
    return citiesOptions.find((c) => c.id === cityId) || null;
  }, [cityId, citiesOptions]);

  const initialLoadDone = useRef(false);

  const getBoundsFromMap = useCallback((mapInstance) => {
    const b = mapInstance?.getBounds();
    if (!b) return null;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return { swLng: sw.lng(), swLat: sw.lat(), neLng: ne.lng(), neLat: ne.lat() };
  }, []);

  const onMapLoad = useCallback((mapInstance) => setMap(mapInstance), []);

  // Fetch on first idle (bounds are available) and whenever date changes
  const onMapIdle = useCallback(() => {
    if (!map) return;
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      const b = getBoundsFromMap(map);
      if (b) setBounds(b);
    }
  }, [map, getBoundsFromMap]);

  // Re-search when date changes and map is available
  useEffect(() => {
    if (!map || !date || !initialLoadDone.current) return;
    const b = getBoundsFromMap(map);
    if (b) {
      setBounds(b);
      setShowPolygons(false);
      setSelectedRideIds([]);
    }
  }, [map, date, getBoundsFromMap]);

  const onSearchArea = useCallback(() => {
    if (!map) return;
    const b = getBoundsFromMap(map);
    if (b) {
      setBounds(b);
      setShowPolygons(false);
      setSelectedRideIds([]);
    }
  }, [map, getBoundsFromMap]);

  const onCityChange = useCallback(
    (_e, city) => {
      updateURLParams({ date, type, cityId: city?.id }, history);
      if (city && map && city.cityCoord?.coordinates) {
        const [lng, lat] = city.cityCoord.coordinates;
        map.panTo({ lat, lng });
        map.setZoom(11);
      }
    },
    [map, date, type, history],
  );

  const onDateChange = useCallback(
    (e) => {
      updateURLParams({ date: e.target.value, type, cityId }, history);
    },
    [type, cityId, history],
  );

  const onTypeChange = useCallback(
    (_e, v) => {
      updateURLParams({ date, type: v || 'ALL', cityId }, history);
    },
    [date, cityId, history],
  );

  // Fetch pins for current viewport
  const {
    data: pinsData,
    isLoading: loadingPins,
  } = useTanStackQuery({
    queryKey: ['isochrone-pins', bounds, date],
    queryFn: () => isochroneApi.fetchPins({ ...bounds, date }),
    enabled: !!bounds && !!date,
  });

  const allPins = pinsData?.pins || [];
  const pins = type === 'ALL' ? allPins : allPins.filter((p) => p.type === type);

  // Collect all visible ride IDs and assign stable colors
  const visibleRideIds = useMemo(() => {
    return [...new Set(pins.map((p) => p.mongo_ride_id))];
  }, [pins]);

  const rideColorMap = useMemo(() => {
    const colorMap = new window.Map();
    visibleRideIds.forEach((id) => getRideColor(id, colorMap));
    return colorMap;
  }, [visibleRideIds]);

  // Fetch polygons when requested
  const rideIdsToFetch = showPolygons ? visibleRideIds : selectedRideIds;
  const {
    data: polygonsData,
    isLoading: loadingPolygons,
  } = useTanStackQuery({
    queryKey: ['isochrone-polygons', rideIdsToFetch],
    queryFn: () => isochroneApi.fetchPolygons(rideIdsToFetch),
    enabled: rideIdsToFetch.length > 0,
  });

  const allPolygons = polygonsData?.isochrones || [];
  const polygons = type === 'ALL' ? allPolygons : allPolygons.filter((p) => p.type === type);

  const onShowPolygons = useCallback(() => {
    setShowPolygons(true);
    setSelectedRideIds([]);
  }, []);

  const onClearPolygons = useCallback(() => {
    setShowPolygons(false);
    setSelectedRideIds([]);
  }, []);

  const onPinClick = useCallback((pin) => {
    setShowPolygons(false);
    setSelectedRideIds((prev) => {
      if (prev.includes(pin.mongo_ride_id)) return prev;
      return [...prev, pin.mongo_ride_id];
    });
  }, []);

  const hasPolygons = showPolygons || selectedRideIds.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
            Isochrones
          </Typography>
          <FilterSelect
            icon={<StationIcon />}
            label="City"
            placeholder="Filter by city"
            clearLabel="All cities"
            options={citiesOptions}
            value={selectedCity}
            onChange={(v) => onCityChange(null, v)}
            getOptionLabel={(c) => `${c.cityName}, ${c.stateAbbr}`}
          />
          <FilterSelect
            icon={<InfoIcon />}
            label="Type"
            placeholder="Type"
            clearLabel="All types"
            searchable={false}
            options={TYPE_OPTIONS.filter((t) => t !== 'ALL')}
            value={type === 'ALL' ? null : type}
            onChange={(v) => onTypeChange(null, v)}
          />
          <DatePicker pill id="isochrone_date" value={date} onChange={onDateChange} />
          <Button onClick={onSearchArea} disabled={!map || !date} sx={pillButtonSx}>
            Search Area
          </Button>
          <Button onClick={onShowPolygons} disabled={pins.length === 0 || showPolygons} sx={filterPillSx}>
            Show Polygons
          </Button>
          <Button onClick={onClearPolygons} disabled={!hasPolygons} sx={filterPillSx}>
            Clear Polygons
          </Button>
        </Stack>
      </Box>
      {(loadingPins || loadingPolygons) && (
        <Typography variant="body2" sx={{ px: 4, pt: 1 }} color="text.secondary">
          Loading...
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}>
        <Map
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onIdle={onMapIdle}
          mapContainerStyle={{ width: '100%', height: '100%' }}
        >
            {/* Pin mode: show markers when no polygons are displayed */}
            {!hasPolygons &&
              pins.map((pin, i) => {
                const color = getRideColor(pin.mongo_ride_id, rideColorMap);
                const isOrigin = pin.type === 'ORIGIN';
                return (
                  <Marker
                    key={`${pin.mongo_ride_id}-${pin.type}-${i}`}
                    position={{ lat: Number(pin.lat), lng: Number(pin.lng) }}
                    icon={makePinIcon(color, isOrigin)}
                    onClick={() => onPinClick(pin)}
                    title={`${pin.type} — Ride ${pin.mongo_ride_id}`}
                  />
                );
              })}

            {/* Polygon mode */}
            {hasPolygons &&
              polygons.map((iso) => {
                const geojson = JSON.parse(iso.geometry_geojson);
                const color = getRideColor(iso.mongo_ride_id, rideColorMap);
                const isOrigin = iso.type === 'ORIGIN';

                // GeoJSON Polygon: coordinates[0] is the outer ring
                const paths = geojson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));

                return (
                  <Polygon
                    key={iso.id}
                    paths={paths}
                    options={{
                      fillColor: color,
                      fillOpacity: isOrigin ? 0.2 : 0.1,
                      strokeColor: color,
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      strokeDashArray: isOrigin ? null : '4 4',
                    }}
                  />
                );
              })}
        </Map>
      </Box>
    </Box>
  );
};

export default Isochrones;
